import { useCallback, useContext, useEffect, useRef, useState } from "react"
import { UNSAFE_NavigationContext } from "react-router-dom"

type BlockerState = "blocked" | "unblocked"

export interface UnsavedChangesBlocker {
  /** "blocked" while an in-app navigation is being held for the user to decide. */
  state: BlockerState
  /** Let the held navigation through (e.g. "leave without saving"). */
  proceed: () => void
  /** Cancel the held navigation and stay on the page. */
  reset: () => void
  /**
   * Exempt the very next in-app navigation from the guard. Call this right
   * before a programmatic `navigate()` that the page itself initiates (e.g. the
   * post-save redirect) so it isn't intercepted and bounced into the modal.
   */
  allowNextNavigation: () => void
}

/**
 * Guards against losing unsaved work when `when` is true.
 *
 * The app uses the component `<BrowserRouter>`, not a data router, so
 * `useBlocker` is unavailable. This reimplements the same idea for component
 * routers by patching the history navigator: in-app `navigate()`/`<Link>`
 * clicks (which call `navigator.push`/`replace`) are intercepted and held in
 * `state: "blocked"` so the caller can render its own confirmation UI, then
 * `proceed()` or `reset()`. A `beforeunload` listener covers hard navigations
 * (refresh, tab close, typing a new URL) with the browser's native prompt.
 *
 * Known limitation: the browser Back/Forward button (a history POP) is not
 * intercepted for in-app routes — only push/replace and full-page unloads are.
 */
export function useUnsavedChangesPrompt(when: boolean): UnsavedChangesBlocker {
  const { navigator } = useContext(UNSAFE_NavigationContext) as {
    navigator: {
      push: (...args: unknown[]) => void
      replace: (...args: unknown[]) => void
    }
  }
  // Live mirror of `when` so the patched navigator reads the current value
  // without reinstalling the patch on every toggle.
  const whenRef = useRef(when)
  whenRef.current = when

  const pendingRef = useRef<(() => void) | null>(null)
  const bypassOnceRef = useRef(false)
  const [blocked, setBlocked] = useState(false)

  // Hard navigations (refresh / close tab / external URL): native prompt only.
  useEffect(() => {
    if (!when) return undefined
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ""
    }
    window.addEventListener("beforeunload", onBeforeUnload)
    return () => window.removeEventListener("beforeunload", onBeforeUnload)
  }, [when])

  // In-app navigations: hold the call and surface it as `state: "blocked"`.
  useEffect(() => {
    const originalPush = navigator.push
    const originalReplace = navigator.replace
    const intercept =
      (original: (...args: unknown[]) => void) =>
      (...args: unknown[]) => {
        if (!whenRef.current || bypassOnceRef.current) {
          bypassOnceRef.current = false
          original.apply(navigator, args)
          return
        }
        pendingRef.current = () => original.apply(navigator, args)
        setBlocked(true)
      }
    navigator.push = intercept(originalPush)
    navigator.replace = intercept(originalReplace)
    return () => {
      navigator.push = originalPush
      navigator.replace = originalReplace
    }
  }, [navigator])

  const proceed = useCallback(() => {
    const go = pendingRef.current
    pendingRef.current = null
    setBlocked(false)
    // Run via the captured original push/replace, bypassing the patch.
    go?.()
  }, [])

  const reset = useCallback(() => {
    pendingRef.current = null
    setBlocked(false)
  }, [])

  const allowNextNavigation = useCallback(() => {
    bypassOnceRef.current = true
  }, [])

  return {
    state: blocked ? "blocked" : "unblocked",
    proceed,
    reset,
    allowNextNavigation,
  }
}
